import { useEffect } from "react";
import { useLocation, useMatches } from "react-router-dom";
import { storeSaveRefer } from "src/utils/store";

export const BRAND_TITLE = "Subscan Open Platform";
const DEFAULT_DESCRIPTION =
  "The management hub for Subscan Explorer — manage API subscriptions, alerts, and identities in one place to streamline blockchain data services.";

export type PageHandle = {
  title?: string;
  description?: string;
  disableSuffix?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isPageHandle = (value: unknown): value is PageHandle => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.title === "string" || typeof value.description === "string" || typeof value.disableSuffix === "boolean"
  );
};

export const PageMetadata = () => {
  const matches = useMatches();
  const { search } = useLocation();

  useEffect(() => {
    const refer = new URLSearchParams(search).get("refer");
    if (refer) {
      storeSaveRefer(refer);
    }
  }, [search]);

  useEffect(() => {
    const matchWithHandle = [...matches].reverse().find((match) => isPageHandle(match.handle));
    const handle = matchWithHandle?.handle as PageHandle | undefined;

    const title = handle?.title;
    const disableSuffix = handle?.disableSuffix;
    const description = handle?.description ?? DEFAULT_DESCRIPTION;

    const formattedTitle = title ? (disableSuffix ? title : `${title} | ${BRAND_TITLE}`) : BRAND_TITLE;

    if (document.title !== formattedTitle) {
      document.title = formattedTitle;
    }

    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.setAttribute("name", "description");
      document.head.appendChild(metaTag);
    }

    if (metaTag.getAttribute("content") !== description) {
      metaTag.setAttribute("content", description);
    }
  }, [matches]);

  return null;
};
